/**
 * FollowupExecutionCard — Story 7.10b.
 *
 * Renders a single row from `followup_executions` as a compact card within
 * the conversation drill-down. Consumed by ConversationDetailPage when a
 * conversation has one or more follow-up attempts.
 */

import {
  FollowupStatusBadge,
  type FollowupExecutionStatus,
} from '@/components/followups/FollowupStatusBadge';
import { formatDateTime as fmtDateTime } from '@/lib/format-date';

export interface FollowupExecutionListItem {
  id: string;
  attemptedAt: string;
  status: FollowupExecutionStatus;
  errorMessage: string | null;
  messageContentSnapshot: string;
  position: number;
  campaignName: string;
}

function formatDateTime(iso: string | null): string {
  return fmtDateTime(iso) || '—';
}

const SNIPPET_LEN = 120;

export function FollowupExecutionCard({ execution }: { execution: FollowupExecutionListItem }) {
  const snippet =
    execution.messageContentSnapshot.length > SNIPPET_LEN
      ? `${execution.messageContentSnapshot.slice(0, SNIPPET_LEN)}…`
      : execution.messageContentSnapshot;

  return (
    <li
      className="flex flex-col gap-1 rounded border bg-background p-3 text-sm"
      data-testid={`followup-exec-${execution.id}`}
    >
      <div className="flex items-center gap-2">
        <FollowupStatusBadge status={execution.status} />
        <span className="text-xs text-muted-foreground">
          {formatDateTime(execution.attemptedAt)}
        </span>
        <span className="ml-auto text-xs text-muted-foreground">#{execution.position}</span>
      </div>
      <p className="whitespace-pre-wrap text-xs text-muted-foreground">{snippet}</p>
      {execution.errorMessage ? (
        <p className="rounded bg-destructive/10 px-2 py-1 text-xs text-destructive">
          {execution.errorMessage}
        </p>
      ) : null}
    </li>
  );
}
